import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { RootStackParamList } from '../types';
import { adminService } from '../services/admin';
import { useDialog } from '../contexts';
import { Button } from '../components';

type Props = NativeStackScreenProps<RootStackParamList, 'AdminSettings'>;

interface ClubswapFeeSetting {
  amount: number;
  currency: string;
}

type PayoutInterval = 'default' | 'weekly' | 'monthly';

const PAYOUT_INTERVALS: PayoutInterval[] = ['default', 'weekly', 'monthly'];

const MAX_CLUBSWAP_FEE = 5.0;
const MIN_CLUBSWAP_FEE = 0.0;

export const AdminSettingsScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clubswapFee, setClubswapFee] = useState('');
  const [currentFee, setCurrentFee] = useState<number>(1.0);
  const [payoutInterval, setPayoutInterval] = useState<PayoutInterval>('default');
  const [currentPayoutInterval, setCurrentPayoutInterval] = useState<PayoutInterval>('default');

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    const isAdmin = await adminService.isCurrentUserAdmin();
    if (!isAdmin) {
      await dialog.alert(
        t('common.errors.unauthorized'),
        t('screens.adminDashboard.notAuthorized'),
        'error'
      );
      navigation.goBack();
      return;
    }

    await loadSettings();
  };

  const loadSettings = async () => {
    try {
      const feeData = await adminService.getAdminSetting('default_clubswap_fee');
      const fee = feeData?.amount || 1.0;
      setCurrentFee(fee);
      setClubswapFee(fee.toFixed(2));

      const interval = await loadPayoutSchedule();
      setCurrentPayoutInterval(interval);
      setPayoutInterval(interval);
    } catch (error: any) {
      await dialog.alert(
        t('common.errors.error'),
        error.message,
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadPayoutSchedule = async (): Promise<PayoutInterval> => {
    try {
      const data = await adminService.getAdminSetting('stripe_payout_schedule');
      const interval = data?.interval;
      if (interval === 'weekly' || interval === 'monthly' || interval === 'default') {
        return interval;
      }
      return 'default';
    } catch {
      return 'default';
    }
  };

  const validateFee = (value: string): boolean => {
    const num = parseFloat(value);
    if (isNaN(num)) {
      return false;
    }
    return num >= MIN_CLUBSWAP_FEE && num <= MAX_CLUBSWAP_FEE;
  };

  const handleSave = async () => {
    if (!clubswapFee.trim()) {
      await dialog.alert(
        t('common.errors.error'),
        t('screens.adminSettings.errorFeeRequired'),
        'error'
      );
      return;
    }

    const feeAmount = parseFloat(clubswapFee);

    if (isNaN(feeAmount)) {
      await dialog.alert(
        t('common.errors.error'),
        t('screens.adminSettings.errorInvalidFee'),
        'error'
      );
      return;
    }

    if (!validateFee(clubswapFee)) {
      await dialog.alert(
        t('common.errors.error'),
        t('screens.adminSettings.errorFeeRange', {
          min: MIN_CLUBSWAP_FEE.toFixed(2),
          max: MAX_CLUBSWAP_FEE.toFixed(2),
        }),
        'error'
      );
      return;
    }

    setSaving(true);
    try {
      await adminService.updateAdminSetting('default_clubswap_fee', {
        amount: feeAmount,
        currency: 'EUR',
      });

      await adminService.updateAdminSetting('stripe_payout_schedule', {
        interval: payoutInterval,
      });

      setCurrentFee(feeAmount);
      setCurrentPayoutInterval(payoutInterval);

      await dialog.alert(
        t('common.success'),
        t('screens.adminSettings.successSave'),
        'success'
      );
    } catch (error: any) {
      // Fee and schedule are saved as two independent settings. If one persists
      // and the other fails, re-read both from the server so the form's
      // "current" markers (and hasChanges) reflect what actually persisted
      // rather than showing a half-applied change as still pending.
      try {
        const feeData = await adminService.getAdminSetting('default_clubswap_fee');
        const fee = feeData?.amount || 1.0;
        setCurrentFee(fee);
        setClubswapFee(fee.toFixed(2));
        const interval = await loadPayoutSchedule();
        setCurrentPayoutInterval(interval);
        setPayoutInterval(interval);
      } catch {
        // Re-read failed too; leave state as-is.
      }
      await dialog.alert(
        t('common.errors.error'),
        error.message,
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setClubswapFee(currentFee.toFixed(2));
    setPayoutInterval(currentPayoutInterval);
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#007AFF" />
      </View>
    );
  }

  const hasChanges =
    clubswapFee !== currentFee.toFixed(2) || payoutInterval !== currentPayoutInterval;

  return (
    <View style={styles.wrapper}>
      <View style={[styles.headerContainer, Platform.OS === 'web' && styles.headerContainerWeb]}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AdminDashboard')}
          >
            <Ionicons name="arrow-back" size={24} color="#007AFF" />
            <Text style={styles.backText}>{t('common.buttons.back')}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('screens.adminSettings.title')}</Text>
        </View>
      </View>

      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.contentContainer}>
          <Text style={styles.subtitle}>
            {t('screens.adminSettings.subtitle')}
          </Text>

          <View style={styles.card}>
            <Text style={styles.cardTitle}>
              {t('screens.adminSettings.clubswapFeeTitle')}
            </Text>
            <Text style={styles.cardDescription}>
              {t('screens.adminSettings.clubswapFeeDescription')}
            </Text>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>
                {t('screens.adminSettings.feeAmount')}
              </Text>
              <View style={styles.inputRow}>
                <Text style={styles.currencySymbol}>€</Text>
                <TextInput
                  style={styles.input}
                  value={clubswapFee}
                  onChangeText={setClubswapFee}
                  keyboardType="decimal-pad"
                  placeholder="0.00"
                />
              </View>
              <Text style={styles.hint}>
                {t('screens.adminSettings.feeRangeHint', {
                  min: MIN_CLUBSWAP_FEE.toFixed(2),
                  max: MAX_CLUBSWAP_FEE.toFixed(2),
                })}
              </Text>
            </View>

            <View style={styles.currentFeeContainer}>
              <Text style={styles.currentFeeLabel}>
                {t('screens.adminSettings.currentFee')}
              </Text>
              <Text style={styles.currentFeeValue}>€{currentFee.toFixed(2)}</Text>
            </View>

          </View>

          <View style={styles.card}>
            <Text style={styles.cardTitle}>
              {t('screens.adminSettings.payoutScheduleTitle')}
            </Text>

            {PAYOUT_INTERVALS.map((interval) => {
              const selected = payoutInterval === interval;
              const labelKey =
                interval === 'weekly'
                  ? 'payoutScheduleWeekly'
                  : interval === 'monthly'
                    ? 'payoutScheduleMonthly'
                    : 'payoutScheduleDefault';
              return (
                <TouchableOpacity
                  key={interval}
                  style={styles.radioRow}
                  onPress={() => setPayoutInterval(interval)}
                >
                  <Ionicons
                    name={selected ? 'radio-button-on' : 'radio-button-off'}
                    size={22}
                    color={selected ? '#007AFF' : '#999'}
                  />
                  <Text style={styles.radioLabel}>
                    {t(`screens.adminSettings.${labelKey}`)}
                  </Text>
                </TouchableOpacity>
              );
            })}

            <Text style={styles.hint}>
              {t('screens.adminSettings.payoutScheduleHelper')}
            </Text>
          </View>

          <View style={styles.actions}>
            {hasChanges && (
              <Button
                title={t('common.buttons.reset')}
                onPress={handleReset}
                variant="secondary"
              />
            )}
            <Button
              title={t('common.buttons.save')}
              onPress={handleSave}
              loading={saving}
              variant="primary"
              disabled={!hasChanges}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerContainer: {
    padding: 16,
    paddingTop: Platform.OS === 'web' ? 16 : 50,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerContainerWeb: {
    alignItems: 'center',
  },
  headerContent: {
    width: '100%',
    maxWidth: 1200,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  backText: {
    fontSize: 16,
    color: '#007AFF',
    marginLeft: 4,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#333',
  },
  container: {
    flex: 1,
  },
  scrollContent: {
    ...(Platform.OS === 'web' && {
      alignItems: 'center',
    }),
  },
  contentContainer: {
    width: '100%',
    padding: 16,
    ...(Platform.OS === 'web' && {
      maxWidth: 1200,
    }),
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    marginBottom: 24,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  radioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
  },
  radioLabel: {
    flex: 1,
    fontSize: 14,
    color: '#333',
    marginLeft: 12,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 8,
    color: '#333',
  },
  cardDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 24,
  },
  inputGroup: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  currencySymbol: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginRight: 8,
  },
  input: {
    flex: 1,
    fontSize: 18,
    paddingVertical: 12,
    color: '#333',
    outlineStyle: undefined,
  },
  hint: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  currentFeeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    padding: 16,
    borderRadius: 8,
    marginBottom: 24,
  },
  currentFeeLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
  },
  currentFeeValue: {
    fontSize: 20,
    fontWeight: '600',
    color: '#007AFF',
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
  },
});
